'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import {
  Compass,
  ClipboardCheck,
  FileBarChart,
  ArrowRight,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const pillars = [
  {
    icon: Compass,
    title: 'Guide',
    subtitle: 'Contextual Guidance',
    description:
      'Navigate all 41 Contributing Outcomes and ~300 IGPs with plain-English explanations, OT-specific considerations, and evidence examples for every indicator.',
    features: [
      'Plain-English explanations',
      'Why it matters context',
      'Common evidence types',
      'OT-specific guidance',
    ],
    color: 'accent',
    gradient: 'from-accent/10 to-transparent',
  },
  {
    icon: ClipboardCheck,
    title: 'Assess',
    subtitle: 'Multi-System Support',
    description:
      'Manage assessments across multiple OT systems with automatic status rollup from IGP level through to Contributing Outcome, Principle, and Objective.',
    features: [
      'Multi-system tracking',
      'Automatic rollup logic',
      'PA/NA distinction',
      'Evidence referencing',
    ],
    color: 'chart-2',
    gradient: 'from-[hsl(225_60%_45%/0.1)] to-transparent',
  },
  {
    icon: FileBarChart,
    title: 'Report',
    subtitle: 'Structured Outputs',
    description:
      'Generate PDF and Excel exports structured for regulator submission, with progress dashboards and gap analysis built in.',
    features: [
      'PDF reports',
      'Excel exports',
      'Progress dashboard',
      'Gap analysis view',
    ],
    color: 'success',
    gradient: 'from-success/10 to-transparent',
  },
];

function ProductPreview() {
  return (
    <div className="relative mx-auto max-w-5xl">
      {/* Browser frame */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-border bg-secondary/50 px-4 py-3">
          <div className="flex gap-1.5">
            <div className="h-3 w-3 rounded-full bg-destructive/60" />
            <div className="h-3 w-3 rounded-full bg-warning/60" />
            <div className="h-3 w-3 rounded-full bg-success/60" />
          </div>
          <div className="mx-auto flex items-center gap-2 rounded-md bg-background px-3 py-1">
            <div className="h-3 w-3 rounded-full bg-success" />
            <span className="font-mono text-xs text-muted-foreground">
              CAF-Pilot Assessment Platform
            </span>
          </div>
        </div>

        {/* App content mockup */}
        <div className="grid lg:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <div className="hidden border-r border-border bg-secondary/30 p-4 lg:block">
            <div className="space-y-1">
              {['Objective A', 'Objective B', 'Objective C', 'Objective D'].map(
                (obj, i) => (
                  <div
                    key={obj}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                      i === 1
                        ? 'bg-accent/10 text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${
                        i === 0
                          ? 'bg-success'
                          : i === 1
                            ? 'bg-warning'
                            : 'bg-muted-foreground/30'
                      }`}
                    />
                    <span className="text-sm font-medium">{obj}</span>
                  </div>
                )
              )}
            </div>

            <div className="mt-6 space-y-1 pl-4">
              {['B1 - Service Protection', 'B2 - Identity & Access', 'B3 - Data Security'].map(
                (item, i) => (
                  <div
                    key={item}
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${
                      i === 1
                        ? 'bg-accent/5 font-medium text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    <div
                      className={`h-1.5 w-1.5 rounded-full ${
                        i === 0 ? 'bg-success' : 'bg-warning'
                      }`}
                    />
                    {item}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Main content */}
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded bg-accent/10 px-2 py-0.5 font-mono text-xs font-medium text-accent">
                    B2.c
                  </span>
                  <span className="rounded bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                    Partially Achieved
                  </span>
                </div>
                <h3 className="mt-2 font-heading text-lg font-semibold text-foreground">
                  Privileged User Management
                </h3>
              </div>
              <Button size="sm" variant="outline" className="text-xs">
                <FileBarChart className="mr-1.5 h-3.5 w-3.5" />
                Export
              </Button>
            </div>

            {/* IGP Table */}
            <div className="mt-6 overflow-hidden rounded-lg border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-secondary/50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      IGP
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Sys 1
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Sys 2
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Overall
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    { id: 'IGP-B2C-NA01', sys1: 'success', sys2: 'success', overall: 'success' },
                    { id: 'IGP-B2C-NA02', sys1: 'success', sys2: 'warning', overall: 'warning' },
                    { id: 'IGP-B2C-PA03', sys1: 'success', sys2: 'success', overall: 'success' },
                  ].map((row) => (
                    <tr key={row.id} className="hover:bg-secondary/30">
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {row.id}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div
                          className={`mx-auto h-4 w-4 rounded-full ${
                            row.sys1 === 'success'
                              ? 'bg-success'
                              : row.sys1 === 'warning'
                                ? 'bg-warning'
                                : 'bg-destructive'
                          }`}
                        />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div
                          className={`mx-auto h-4 w-4 rounded-full ${
                            row.sys2 === 'success'
                              ? 'bg-success'
                              : row.sys2 === 'warning'
                                ? 'bg-warning'
                                : 'bg-destructive'
                          }`}
                        />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div
                          className={`mx-auto h-4 w-4 rounded-full ${
                            row.overall === 'success'
                              ? 'bg-success'
                              : row.overall === 'warning'
                                ? 'bg-warning'
                                : 'bg-destructive'
                          }`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Help panel */}
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-accent/20 bg-accent/5 p-4">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  IGP-B2C-NA02 · Plain-English Guidance
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This checks whether privileged access uses generic or shared
                  accounts. Document compensating controls or remediation plans.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full rounded-xl border border-accent/20 bg-accent/5" />
      <div className="absolute -bottom-8 -right-8 -z-20 h-full w-full rounded-xl border border-border bg-secondary/50" />
    </div>
  );
}

export function SolutionSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden py-24 lg:py-32" id="solution">
      <div className="absolute inset-0 bg-mesh-1" />

      <div ref={containerRef} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-1.5"
          >
            <Sparkles className="h-4 w-4 text-accent" />
            <span className="font-heading text-sm font-medium text-foreground">
              The Solution
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
            Structured CAF assessment,{' '}
            <span className="italic text-accent">start to finish</span>
          </h2>

          <p className="mt-6 text-lg text-muted-foreground">
            A purpose-built platform that guides your team through the full CAF
            assessment process — structured guidance, multi-system scoring, and
            outputs structured for submission.
          </p>
        </motion.div>

        {/* Three Pillars */}
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:shadow-xl"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${pillar.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-100`}
                />

                <div className="relative">
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-xl bg-${pillar.color}/10 transition-transform duration-300 group-hover:scale-110`}
                  >
                    <Icon
                      className={`h-7 w-7 text-${pillar.color}`}
                      strokeWidth={1.5}
                    />
                  </div>

                  <div className="mt-6">
                    <h3 className="font-display text-2xl text-foreground">
                      {pillar.title}
                    </h3>
                    <p className="mt-1 text-sm font-medium text-muted-foreground">
                      {pillar.subtitle}
                    </p>
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </p>

                  <ul className="mt-6 space-y-2">
                    {pillar.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm text-foreground"
                      >
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex items-center gap-2 text-sm font-medium text-accent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Learn more
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Product Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20"
        >
          <ProductPreview />
        </motion.div>
      </div>
    </section>
  );
}
